import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useApp } from './AppContext';
import ProtectedRoute from './components/ProtectedRoute';
import ToolsLanding from './pages/ToolsLanding';
import Auth from './pages/Auth';
import AuthComplete from './pages/AuthComplete';
import Landing from './pages/Landing';
import Overview from './pages/Overview';
import CategorizeTransactions from './pages/CategorizeTransactions';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import SideHustleHome from './pages/SideHustle/Home';
import TalentsPassions from './pages/SideHustle/TalentsPassions';
import ProblemIdentifier from './pages/SideHustle/ProblemIdentifier';
import BusinessCanvas from './pages/SideHustle/BusinessCanvas';
import BusinessBudgeting from './pages/SideHustle/BusinessBudgeting';
import TakingTheLeap from './pages/SideHustle/TakingTheLeap';
import DreamingHub from './pages/SideHustle/DreamingHub';

// Component that redirects logged-in users away from auth page
const AuthRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, loading } = useApp();

  // Get tool parameter from URL
  const searchParams = new URLSearchParams(window.location.search);
  const tool = searchParams.get('tool') || 'mmt';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="text-2xl font-semibent text-primary">Loading...</div>
      </div>
    );
  }

  if (currentUser) {
    // Redirect based on tool parameter
    if (tool === 'side-hustle') {
      return <Navigate to="/side-hustle/home" replace />;
    }
    return <Navigate to="/mmt/landing" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<ToolsLanding />} />
        <Route
          path="/auth"
          element={
            <AuthRoute>
              <Auth />
            </AuthRoute>
          }
        />
        <Route path="/auth/complete" element={<AuthComplete />} />

        {/* MMT Protected routes */}
        <Route
          path="/mmt/landing"
          element={
            <ProtectedRoute>
              <Landing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mmt/overview"
          element={
            <ProtectedRoute>
              <Overview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mmt/categorize"
          element={
            <ProtectedRoute>
              <CategorizeTransactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mmt/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mmt/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* Side Hustle Protected routes */}
        <Route
          path="/side-hustle/home"
          element={
            <ProtectedRoute>
              <SideHustleHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/side-hustle/talents"
          element={
            <ProtectedRoute>
              <TalentsPassions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/side-hustle/problems"
          element={
            <ProtectedRoute>
              <ProblemIdentifier />
            </ProtectedRoute>
          }
        />
        <Route
          path="/side-hustle/canvas"
          element={
            <ProtectedRoute>
              <BusinessCanvas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/side-hustle/budgeting"
          element={
            <ProtectedRoute>
              <BusinessBudgeting />
            </ProtectedRoute>
          }
        />
        <Route
          path="/side-hustle/leap"
          element={
            <ProtectedRoute>
              <TakingTheLeap />
            </ProtectedRoute>
          }
        />
        <Route
          path="/side-hustle/dreams"
          element={
            <ProtectedRoute>
              <DreamingHub />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AppProvider>
      <AppRoutes />
    </AppProvider>
  );
}

export default App;
